import { motion } from "framer-motion";
import {
  ScrollText, BookOpen, BadgeCheck, MessageSquareWarning, Accessibility, FileCheck, Stamp, Download,
} from "lucide-react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { easeOut } from "@/components/common/Reveal";
import { DOCUMENTS, REFERENT_HANDICAP } from "@/data/site";
import { useLead } from "@/components/common/LeadModal";

const ICONS = { ScrollText, BookOpen, BadgeCheck, MessageSquareWarning, Accessibility, FileCheck, Stamp };

export default function DocumentsUtiles() {
  const { openLead } = useLead();
  return (
    <>
      <Seo
        title="Documents utiles"
        description="Règlement intérieur, livret d'accueil, charte qualité, procédure de réclamation et procédure d'accessibilité handicap d'EDUCASER GROUP."
        path="/documents-utiles"
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Documents utiles" }]}
        eyebrow="Ressources & qualité"
        title="Documents utiles"
        intro="Retrouvez nos documents de référence. Ils sont disponibles sur simple demande auprès de nos équipes."
      />

      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {DOCUMENTS.map((d, i) => {
              const Icon = ICONS[d.icon] || FileCheck;
              return (
                <motion.div
                  key={d.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease: easeOut, delay: (i % 3) * 0.08 }}
                  className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,35,102,0.12)]"
                  data-testid={`document-${d.id}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-brand-blue">{d.titre}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{d.description}</p>
                  <button
                    onClick={() => openLead("document", { document: d.titre })}
                    className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-brand-red px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-red-light"
                    data-testid={`document-request-${d.id}`}
                  >
                    <Download className="h-4 w-4" /> Demander ce document
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Référent handicap */}
          <div className="mt-16 rounded-3xl bg-brand-blue px-8 py-12 text-white sm:px-14" data-testid="referent-handicap">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10">
                <Accessibility className="h-6 w-6 text-brand-red-light" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold">Accessibilité & référent handicap</h2>
                <p className="mt-3 max-w-2xl text-white/80">
                  Nos formations peuvent être adaptées aux personnes en situation de handicap. Notre
                  référente handicap, <strong className="text-white">{REFERENT_HANDICAP.nom}</strong>, étudie
                  avec vous les modalités d'accueil, d'aménagement et les partenaires mobilisables.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a href={`tel:${REFERENT_HANDICAP.telephone.replace(/\s/g, "")}`} className="font-medium text-white hover:text-brand-red-light">
                    Téléphone : {REFERENT_HANDICAP.telephone}
                  </a>
                  <a href={`mailto:${REFERENT_HANDICAP.email}`} className="font-medium text-white hover:text-brand-red-light">
                    E-mail : {REFERENT_HANDICAP.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
